import { useEffect, useMemo, useRef, useState } from 'react';

import BallotOutlinedIcon from '@mui/icons-material/BallotOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Box, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import Button from '@/components/Buttons/Button';
import { countCsvWidgetExportRows } from '@/components/CsvTableWidget/CsvWidgetExport/downloadCsvWidgetExport';
import { useCsvWidgetExport } from '@/components/CsvTableWidget/CsvWidgetExport/useCsvWidgetExport';
import { NameInput } from '@/components/NameInput';
import { EXPORT_NAME_MAX_LENGTH } from '@/constants/name';
import { pastExportsOptions } from '@/features/DataHub/api';
import { DataHubCard } from '@/features/DataHub/components/DataHubCard';
import { DataHubHeader } from '@/features/DataHub/components/DataHubHeader';
import { DataHubSection } from '@/features/DataHub/components/DataHubSection';
import { DATAHUB_SPACING_LG } from '@/features/DataHub/constants/ui';
import { useDataHubUserConfig } from '@/features/DataHub/hooks/useDataHubUserConfig';
import DateSettings from '@/features/DataHub/modules/Export/components/ExportSettings';
import {
  useExportRequest,
  useExportSettings
} from '@/features/DataHub/modules/Export/context/exportContexts';
import {
  createUniqueExportNameFromBaseName,
  validateExportNameWithMaxLength
} from '@/features/DataHub/modules/Export/services/ExportNameService';
import { ExportTypes } from '@/features/DataHub/modules/Export/types/context.types';
import {
  getDateRangeValidation,
  getFromToValues,
  parseSelectedRange
} from '@/features/DataHub/modules/Export/utils';
import { normalizeSourceBackedDataExportFrom } from '@/features/DataHub/utils/sourceBackedDataExport';
import { useAppDispatch, useAppSelector } from '@/hooks/redux-hooks';
import { closeDrawer } from '@/slices/drawerSlice';

export const CsvWidgetExportView = () => {
  const dispatch = useAppDispatch();
  const csvWidgetExportContext = useAppSelector(state => state.drawer.csvWidgetExportContext);
  const source = csvWidgetExportContext?.source ?? null;
  const dateColumnKey = csvWidgetExportContext?.dateColumnKey ?? null;
  const tabName = csvWidgetExportContext?.tabName;
  const { exportStatus, setExportStatus } = useExportRequest();
  const { exportSettings, setExportSettings } = useExportSettings();
  const { dateRangeType, from, to, periodNumber } = exportSettings;

  const { data: pastExports = [] } = useQuery(pastExportsOptions.list());
  const { timezoneValue, dateFormatValue } = useDataHubUserConfig();

  const [exportName, setExportName] = useState(() =>
    createUniqueExportNameFromBaseName(tabName ?? '', [], 'TradeData')
  );
  const exportNameInitialized = useRef(false);
  const [initializedDateSettingsSourceKey, setInitializedDateSettingsSourceKey] = useState<
    string | null
  >(null);

  useEffect(() => {
    if (!exportNameInitialized.current && pastExports.length > 0) {
      exportNameInitialized.current = true;
      setExportName(
        createUniqueExportNameFromBaseName(
          tabName ?? '',
          pastExports.map(e => ({ fileName: e.name })),
          'TradeData'
        )
      );
    }
  }, [pastExports, tabName]);
  const exportNameError = useMemo(
    () => validateExportNameWithMaxLength(exportName, EXPORT_NAME_MAX_LENGTH),
    [exportName]
  );

  const sourceKey = useMemo(() => {
    if (!source) return 'none';
    if (source.sourceType === 'query') {
      return `query:${source.queryFileId}:${source.selectedRange ?? ''}`;
    }
    return `datalake:${source.fileName}:${source.groupName}`;
  }, [source]);
  const initialRange = source?.sourceType === 'query' ? source.selectedRange : undefined;
  const parsedInitialRange = useMemo(() => parseSelectedRange(initialRange), [initialRange]);

  const dateFormattingConfig = useMemo(
    () => ({ dateFormat: dateFormatValue, timezone: timezoneValue }),
    [dateFormatValue, timezoneValue]
  );

  const { handleExport } = useCsvWidgetExport(source, tabName, dateColumnKey, dateFormattingConfig);

  useEffect(() => {
    setExportSettings(prev => ({
      ...prev,
      exportType: ExportTypes.DATA,
      dateRangeType: parsedInitialRange.dateRangeType,
      periodNumber: parsedInitialRange.periodNumber,
      period: parsedInitialRange.period,
      from: parsedInitialRange.from,
      to: parsedInitialRange.to
    }));

    setInitializedDateSettingsSourceKey(sourceKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceKey, setExportSettings]);

  const validation = useMemo(
    () => getDateRangeValidation({ dateRangeType, from, to, periodNumber }),
    [dateRangeType, from, to, periodNumber]
  );
  const hasDateSettingsError =
    validation.periodNumberError || validation.fromError || validation.toError;
  const isDateSettingsInitialized = initializedDateSettingsSourceKey === sourceKey;

  const { fromValue, toValue } = useMemo(() => getFromToValues(exportSettings), [exportSettings]);
  const requestTo = toValue || null;
  const requestFrom = normalizeSourceBackedDataExportFrom(fromValue, requestTo);

  const isCountQueryEnabled = Boolean(source) && isDateSettingsInitialized && !hasDateSettingsError;

  const {
    data: selectedItemsCount = 0,
    isLoading: isItemsCountLoading,
    isFetching: isItemsCountFetching
  } = useQuery({
    queryKey: ['csv-widget-export-items-count', source, dateColumnKey, requestFrom, requestTo],
    enabled: isCountQueryEnabled,
    staleTime: 30_000,
    queryFn: async () => {
      if (!source) return 0;
      return countCsvWidgetExportRows(
        source,
        requestFrom ?? undefined,
        requestTo ?? undefined,
        dateColumnKey
      );
    }
  });

  const isItemsCountCalculating = isItemsCountLoading || isItemsCountFetching;
  const showNoDataDateAlert =
    isDateSettingsInitialized &&
    !isItemsCountCalculating &&
    !hasDateSettingsError &&
    selectedItemsCount === 0;

  const isExporting = exportStatus === 'loading';

  const handleConfirm = async () => {
    if (!source || exportNameError || hasDateSettingsError) return;

    setExportStatus('loading');
    try {
      await handleExport(requestFrom, requestTo, exportName);
      dispatch(closeDrawer());
    } catch {
      // snackbar is handled by the trade export mutation
    } finally {
      setExportStatus('idle');
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        justifyContent: 'space-between'
      }}
    >
      <DataHubSection
        scroll
        gap={DATAHUB_SPACING_LG}
        sx={{ p: DATAHUB_SPACING_LG }}
      >
        <DataHubHeader
          subtitle='Set up your export file'
          title='Export configuration'
        />
        <DataHubCard
          expandable={false}
          title={
            <>
              <Box
                component='span'
                sx={{
                  display: 'inline-flex',
                  width: 'fit-content',
                  '& .name-input': { flex: 'none', width: 'fit-content' },
                  '& .name-input .MuiInputBase-formControl': { width: 'auto' },
                  '& .name-input .MuiInputBase-input': {
                    fieldSizing: 'content',
                    minWidth: '14ch',
                    maxWidth: `${EXPORT_NAME_MAX_LENGTH + 1}ch`
                  }
                }}
              >
                <NameInput
                  error={exportNameError ?? undefined}
                  isEmpty={exportName.trim().length === 0}
                  maxLength={EXPORT_NAME_MAX_LENGTH}
                  size={18}
                  value={exportName}
                  onChange={setExportName}
                />
              </Box>
              <EditOutlinedIcon sx={{ fontSize: '16px' }} />
            </>
          }
        >
          {null}
        </DataHubCard>
        {isDateSettingsInitialized && (
          <DateSettings
            key={sourceKey}
            defaultExpanded
            showNoDataAlert={showNoDataDateAlert}
          />
        )}

        <DataHubCard
          expandable={false}
          icon={<BallotOutlinedIcon sx={{ fontSize: '16px' }} />}
          title={
            <>
              Format: <Typography sx={{ fontSize: 14 }}>CSV</Typography>
            </>
          }
        />
      </DataHubSection>

      <Box
        sx={{
          minHeight: '96px',
          width: '100%',
          backgroundColor: 'background.main',
          borderTop: '1px solid',
          borderColor: 'info.dark',
          p: DATAHUB_SPACING_LG,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: DATAHUB_SPACING_LG,
          '& > *': { flex: 1 }
        }}
      >
        <Button
          sx={{ color: 'primary.main', borderColor: 'primary.main' }}
          variant='outlined'
          onClick={() => dispatch(closeDrawer())}
        >
          Cancel
        </Button>
        <Button
          disabled={
            !source ||
            !isDateSettingsInitialized ||
            isExporting ||
            isItemsCountCalculating ||
            hasDateSettingsError ||
            !exportName ||
            Boolean(exportNameError) ||
            selectedItemsCount === 0
          }
          variant='contained'
          onClick={handleConfirm}
        >
          Export
        </Button>
      </Box>
    </Box>
  );
};
